import { Injectable } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ILocalizationData, LOCALIZATION_KEYS } from './localization.data';
import { catchError, filter, map, share, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { isEmpty, isUndefined } from 'lodash';
import {
  defaultAppLocaleLanguage,
  ILocaleLanguage,
  supportedAppLocalesLanguages,
} from '../../constants';
import { WindowRefService } from '../window';
import { CookieService } from '../cookie';
import { DateTimeService } from '../date-time';
import { ErrorHandlerService } from '../error-handler';

@Injectable({
  providedIn: 'root',
})
export class LocalizationService {
  constructor(
    private _translateService: TranslateService,
    private _cookieService: CookieService,
    private _dateTimeService: DateTimeService,
    private _windowRefService: WindowRefService,
    private _errorHandlerService: ErrorHandlerService
  ) {
    this._initializeLocale();
  }

  onLocaleChange(): Observable<string> {
    return this._translateService.onLangChange.pipe(
      filter(
        (langChangeEvent: LangChangeEvent) =>
          langChangeEvent.lang === this.getCurrentLocale()
      ),
      map((langChangeEvent: LangChangeEvent) => langChangeEvent.lang)
    );
  }

  isLocalizationsReady(): Observable<boolean> {
    if (isEmpty(this.getInstantTranslations(this.getCurrentLocale()))) {
      return this._translateService.onLangChange.pipe(
        filter(
          (langChangeEvent: LangChangeEvent) =>
            langChangeEvent.lang === this.getCurrentLocale()
        ),
        map(
          (langChangeEvent: LangChangeEvent) =>
            !isUndefined(langChangeEvent.translations)
        ),
        take(1)
      );
    } else {
      return of(true);
    }
  }

  getSupportedCountry(country: string = ''): string {
    const browserCultureLang = this._translateService.getBrowserCultureLang();
    const supportedLocaleLanguage =
      supportedAppLocalesLanguages.find(
        (localeLanguage) =>
          localeLanguage.locale.split('-')[1] === country.toUpperCase()
      ) ||
      supportedAppLocalesLanguages.find(
        (localeLanguage) => localeLanguage.locale === browserCultureLang
      ) ||
      defaultAppLocaleLanguage;

    return supportedLocaleLanguage.locale.split('-')[1];
  }

  /**
   * Returns the best supported locale based on the following priorities:
   *  - case insensitive match
   *  - browser culture language match, e.g. "de-DE"
   *  - browser language match, e.g. "de"
   *  - if not found, fallback to default locale: "en-US"
   */
  getSupportedLocale(locale: string = ''): string {
    const browserCultureLang = this._translateService.getBrowserCultureLang();
    const browserLang = this._translateService.getBrowserLang();

    const supportedLocaleLanguage =
      supportedAppLocalesLanguages.find(
        (localeLanguage) =>
          localeLanguage.locale.toLowerCase() === locale.toLowerCase()
      ) ||
      supportedAppLocalesLanguages.find(
        (localeLanguage) => localeLanguage.locale === browserCultureLang
      ) ||
      supportedAppLocalesLanguages.find(
        (localeLanguage) => localeLanguage.locale.split('-')[0] === browserLang
      ) ||
      defaultAppLocaleLanguage;

    return supportedLocaleLanguage.locale;
  }

  getInstantTranslations(lang: string): any {
    const translations = this._translateService.translations[lang];

    if (!translations) {
      // notify raygun
      this._errorHandlerService.alertSystem(
        new Error(`Translations for ${lang} not found or empty`)
      );

      return this._translateService.translations[
        this._translateService.getDefaultLang()
      ];
    }

    return translations;
  }

  getTranslations(
    lang: string
  ): any | Observable<{ lang: string; translations: any }> {
    return this._translateService.getTranslation(lang).pipe(
      map((translations) => ({ lang, translations })),
      catchError(() => {
        lang = lang.split('-')[0];

        return this._translateService.getTranslation(lang).pipe(
          map((translations) => ({ lang, translations })),
          catchError(() => {
            const defaultLang = this._translateService.getDefaultLang();
            const translations = this.getInstantTranslations(defaultLang);

            if (translations) {
              return of({ lang: defaultLang, translations });
            } else {
              return this._translateService.getTranslation(defaultLang).pipe(
                map((_translations) => ({
                  lang: defaultLang,
                  translations: _translations,
                }))
              );
            }
          })
        );
      }),
      share()
    );
  }

  getCurrentLocale() {
    return this._translateService.currentLang;
  }

  getLanguage(): string {
    return this.getCurrentLocale().split('-')[0];
  }

  getFormattedLocale(locale: string = 'en_US') {
    locale = locale.replace('_', '-');

    const supportedLocaleLanguage: ILocaleLanguage =
      supportedAppLocalesLanguages.find(
        (localeLanguageInfo: ILocaleLanguage) =>
          localeLanguageInfo.locale === locale
      ) || defaultAppLocaleLanguage;

    return supportedLocaleLanguage.locale;
  }

  setDefaultLanguage() {
    this._translateService.setDefaultLang(defaultAppLocaleLanguage.locale);
  }

  setLocale(locale: string) {
    if (this._translateService.currentLang === locale) {
      return;
    }

    this._cookieService.setCookie(LOCALIZATION_KEYS.LOCALE, locale);
    this._dateTimeService.setDateLocale(locale);

    if (this._translateService.getDefaultLang() !== locale) {
      this.getTranslations(locale)
        .pipe(take(1))
        .subscribe(({ lang }) => {
          this._translateService.use(lang);
        });
    } else {
      this._translateService.use(locale);
    }
  }

  getStoredCountry(): string {
    return this._cookieService.getCookie(LOCALIZATION_KEYS.COUNTRY);
  }

  isSupportedLocale(locale: string) {
    return supportedAppLocalesLanguages.some(
      (localeLanguage) => localeLanguage.locale === locale
    );
  }

  isSupportedCountry(country: string) {
    return supportedAppLocalesLanguages.some(
      (localeLanguage) => localeLanguage.locale.split('-')[1] === country
    );
  }

  getLocalizationData(): ILocalizationData {
    return {
      country:
        this._cookieService.getCookie(LOCALIZATION_KEYS.COUNTRY) ||
        this.getSupportedCountry(),
      defaultLanguage: this._translateService.getDefaultLang(),
      currentLocale: this.getCurrentLocale() || this.getSupportedLocale(),
      supportedLanguages: supportedAppLocalesLanguages,
    };
  }

  getDecimalSeparator(locale: string = 'en_US'): string {
    locale = locale.replace('_', '-');

    const testNumber = 1.1;
    const formatter = this._windowRefService.nativeWindow.Intl.NumberFormat(
      locale
    );
    const formattedNumber = formatter.format(testNumber);

    return formattedNumber.replace(/\d+/g, '');
  }

  private _initializeLocale() {
    this.setDefaultLanguage();

    let locale = this._cookieService
      .getCookie(LOCALIZATION_KEYS.LOCALE)
      .replace('_', '-');
    if (locale && this.isSupportedLocale(locale)) {
      this._translateService.use(locale);
      this._dateTimeService.setDateLocale(locale);
    } else {
      locale = this.getSupportedLocale();
      this.setLocale(locale);
    }
  }
}
